import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { findAccess, history, toTitleCase } from '../../utils';
import { connect } from 'react-redux';
import StoreIcon from '@mui/icons-material/Store';
import { ACTIVE, DEFAULT_ROWS_PER_PAGE, MOVATIC_SHOP } from '../../constants';
import { hardwareColumnsV2 } from '../../tableColumns/hardwareColumns';
import { ISystem } from '../../ts/interface/system.interface';
import { IMode } from '../../ts/interface/mode.interface';
import { IAuth } from '../../ts/interface/admins.interface';
import { IHardware, IHardwareType } from '../../ts/interface/hardware.interface';
import { INewHardwareIndex } from '../../ts/interface/pages/hardware.interface';
import {
  useHardwareRelayQuery,
  HardwareQueryFilter,
  buildHardwareSort,
  mapHardwareToIHardware,
} from '../../hooks/hardware/query/V2/useHardwareRelay';
import {
  useHardwareTypeCounts,
  topHardwareTypeNames,
} from '../../hooks/hardware/query/V2/useHardwareTypeCounts';
import PageContainer from '../../components/Redesing/page-container';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../ts/interface/components/redesing/table.interface';
import { updateHardware } from '../../api/hardware';
import AddIcon from '@mui/icons-material/Add';
import { SearchChip } from '../../ts/interface/components/table.interface';
import customAsyncToast from '../../components/Redesing/custom-async-toast';
import useHardwareTypeSystem from '../../hooks/hardware/query/useHardwareTypeSystem';
import Card from '@mui/material/Card';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  MovaticFieldGroupSelect,
  MovaticFieldGroupSelectFilter,
} from '../../components/Redesing/movatic-field-group';
import useQueryString from '../../hooks/queryParams/useQueryString';
import Button from '@mui/material/Button';
import { Stack } from '@mui/system';
import StoreMallDirectoryIcon from '@mui/icons-material/StoreMallDirectory';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import MultiActionChip from '../../components/Redesing/multi-action-chip';
import Skeleton from '@mui/material/Skeleton';
import { useHardwareProductQuickFilters } from '../../hooks/hardware/query/V2/useHardwareProductQuickFilters';
import { getProductIconByName, getHardwareIconByName } from '../../util/utilsR';
import useMediaQuery from '@mui/material/useMediaQuery';
import { IStatePillsOption } from '../../ts/interface/components/redesing/state-pills.interface';
import useUrlState from '../../hooks/use-url-state';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import { useKeysetPagination } from '../../hooks/use-keyset-pagination';
import {
  QUICK_TYPE_LIMIT,
  isYesNo,
  stateOptions,
  toStates,
  toTypeNames,
  yesNoOptions,
} from './hardwareFilterState';

/**
 * Main Hardware page component
 */
const NewHardware = ({
  system,
  remoteUnlockId,
  remoteLockId,
  remoteRingId,
  remoteActionLoaded,
  systemLoaded,
  systemAccess,
  movaticAccess,
}: INewHardwareIndex) => {
  let { type = system.hardware_type } = useParams<{ type: string }>();
  const { queryParams, deleteQueryParam } = useQueryString();
  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  // Dashboard tiles link here as ?type=<name>&status=active. `status` is seeded
  // because ACTIVE is a valid state everywhere and matches what the tile counted.
  // `type` is deliberately NOT seeded: it never filtered before this change
  // either, and a name that doesn't match a hardware type in scope would empty
  // the list on the main entry point into this page. Making those tiles filter by
  // type is a separate change, and needs the org tile's hardcoded ?type=bike
  // fixed first (dashboard/organizationView/resume.tsx).
  const [filters, setFilters, isCompleted] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    // Key kept as `status` so previously shared URLs still restore; the field and
    // the filter are both called "State".
    status: (queryParams.get('status') ? [ACTIVE] : []) as number[],
    available: '' as string,
    online: '' as string,
    inRental: '' as string,
    types: [] as string[],
    products: [] as string[],
    search: '',
    sortConfig: {
      key: 'created_on',
      direction: 'desc',
    } as ISortConfig,
  });

  const debouncedSearchTerm = useSearchDebounce(filters.search);

  const [exportData, setExportData] = useState<boolean>(false);
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumDevice = useMediaQuery(theme.breakpoints.down('md'));
  const shortDevice = useMediaQuery(theme.breakpoints.down('xs'));
  const isMediumScreen = isMediumDevice || shortDevice;
  const hardwareAccess = findAccess(systemAccess).includes('physical') || movaticAccess;

  const appliedStates = useMemo(() => toStates(filters.status), [filters.status]);
  const appliedTypes = useMemo(() => toTypeNames(filters.types), [filters.types]);
  const appliedProducts = useMemo(
    () => toTypeNames(filters.products),
    [filters.products]
  );

  // Pending filter selections, committed to `filters` on Apply. Each mirrors the
  // shape the applied value has, so Apply is a straight copy.
  const [pendingStates, setPendingStates] = useState<number[]>(() =>
    toStates(filters.status)
  );
  const [pendingTypes, setPendingTypes] = useState<string[]>(() =>
    toTypeNames(filters.types)
  );
  const [availableValue, setAvailableValue] = useState<string>('');
  const [onlineValue, setOnlineValue] = useState<string>('');
  const [inRentalValue, setInRentalValue] = useState<string>('');

  const { data: hardwareTypeData = [], isLoading: isLoadingHardwareTypes } =
    useHardwareTypeSystem(system.id, {
      enabled: true,
    });

  // Every hardware type in use in this system — the full list offered by the
  // Type filter in the Filters menu.
  const typeOptions = useMemo(
    () =>
      (hardwareTypeData as IHardwareType[]).map((item) => ({
        value: item.name,
        label: toTitleCase(item.name),
        labelPlural: toTitleCase(item.name_plural),
      })),
    [hardwareTypeData]
  );

  const typeNames = useMemo(() => typeOptions.map((item) => item.value), [typeOptions]);

  const labelForType = useCallback(
    (name: string) =>
      typeOptions.find((item) => item.value === name)?.label ?? toTitleCase(name),
    [typeOptions]
  );

  // Quick-access toggles show only the most used types in this system.
  const { data: typeCounts, isLoading: isLoadingTypeCounts } = useHardwareTypeCounts({
    typeNames,
    systemId: system.gid,
    enabled: Boolean(system.gid) && typeNames.length > 0,
  });

  // Products in use per type — powers per-product quick toggles when a type
  // has more than one product in the system (e.g. Pedal vs Recumbent bikes).
  const { data: productsInUse, isLoading: isLoadingProductsInUse } =
    useHardwareProductQuickFilters({
      typeNames,
      systemId: system.gid,
      enabled: Boolean(system.gid) && typeNames.length > 0,
    });

  const quickTypeNames = useMemo(() => {
    const ranked = topHardwareTypeNames(typeCounts, QUICK_TYPE_LIMIT);
    if (ranked.length > 0) return ranked;
    // Counts unavailable: while they load show nothing rather than a list that
    // reshuffles, but if the request failed fall back to the plain type list so
    // the toggles don't disappear altogether.
    return isLoadingTypeCounts ? [] : typeNames.slice(0, QUICK_TYPE_LIMIT);
  }, [typeCounts, isLoadingTypeCounts, typeNames]);

  // On URL restore, seed the pending controls from the persisted applied values
  // so the Filters dialog reflects what is actually filtering.
  useEffect(() => {
    if (!isCompleted) return;
    setPendingStates(appliedStates);
    setPendingTypes(appliedTypes);
    setAvailableValue(filters.available ?? '');
    setOnlineValue(filters.online ?? '');
    setInRentalValue(filters.inRental ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleted]);

  const sorting: ISortConfig = useMemo(() => {
    return {
      key: filters.sortConfig.key,
      direction: filters.sortConfig.direction,
    };
  }, [filters.sortConfig.key, filters.sortConfig.direction]);

  const sortConfig = useMemo(
    () => buildHardwareSort(filters.sortConfig.key, filters.sortConfig.direction),
    [filters.sortConfig.key, filters.sortConfig.direction]
  );

  const toggleType = useCallback(
    (name: string, selected: boolean) => {
      resetPagination();
      const next = selected
        ? [...appliedTypes, name]
        : appliedTypes.filter((item) => item !== name);
      setPendingTypes(next);
      setFilters((prevState) => ({ ...prevState, types: next }));
    },
    [appliedTypes, setFilters, resetPagination]
  );

  const toggleProduct = useCallback(
    (productId: string, selected: boolean) => {
      resetPagination();
      const next = selected
        ? [...appliedProducts, productId]
        : appliedProducts.filter((item) => item !== productId);
      setFilters((prevState) => ({ ...prevState, products: next }));
    },
    [appliedProducts, setFilters, resetPagination]
  );

  // Types whose units span more than one product in this system: their quick
  // toggle is replaced by one toggle per product (e.g. Pedal Bikes / Recumbent
  // Bikes instead of a single Bikes chip). Single-product types keep the plain
  // type toggle. Everything is ranked together by unit count.
  const multiProductTypes = useMemo(() => {
    const perType = new Map<string, number>();
    productsInUse.forEach((product) => {
      perType.set(product.typeName, (perType.get(product.typeName) ?? 0) + 1);
    });
    return new Set(
      Array.from(perType.entries())
        .filter(([, count]) => count > 1)
        .map(([name]) => name)
    );
  }, [productsInUse]);

  const quickTypeToggles = useMemo(() => {
    // Render the toggles once, with their final shape: while the product data is
    // still loading, painting type-only chips would reshuffle into per-product
    // chips seconds later (same reasoning as the type-counts loading state above).
    if (isLoadingProductsInUse || isLoadingTypeCounts) return [];
    const typeEntries = (typeCounts ?? [])
      .filter((item) => item.count > 0 && !multiProductTypes.has(item.name))
      .map((item) => ({ kind: 'type' as const, id: item.name, count: item.count }));
    const productEntries = productsInUse
      .filter((product) => multiProductTypes.has(product.typeName))
      .map((product) => ({
        kind: 'product' as const,
        id: product.id,
        name: product.name,
        typeName: product.typeName,
        count: product.count,
      }));
    const ranked = [...typeEntries, ...productEntries]
      .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id))
      .slice(0, QUICK_TYPE_LIMIT);

    // Product data unavailable (loading or request failed): fall back to the
    // plain most-used-types toggles so the chips don't disappear.
    // Product request failed or returned nothing: fall back to the plain
    // most-used-types toggles so the chips don't disappear altogether.
    const entries =
      ranked.length > 0
        ? ranked
        : quickTypeNames.map((name) => ({ kind: 'type' as const, id: name, count: 0 }));

    return entries.map((entry) => {
      if (entry.kind === 'product') {
        const { Icon } = getProductIconByName(entry.name, entry.typeName);
        return {
          label: toTitleCase(entry.name),
          id: entry.id,
          icon: <Icon />,
          // MultiActionChip treats "active" as the untoggled state, so an
          // already-selected product starts inactive.
          defaultActive: !appliedProducts.includes(entry.id),
          onActive: (id: string) => toggleProduct(id, true),
          onInactive: (id: string) => toggleProduct(id, false),
        };
      }
      const option = typeOptions.find((item) => item.value === entry.id);
      const { Icon } = getHardwareIconByName(entry.id);
      return {
        label: option?.labelPlural ?? toTitleCase(entry.id),
        id: entry.id,
        icon: <Icon />,
        // MultiActionChip treats "active" as the untoggled state, so an
        // already-selected type starts inactive.
        defaultActive: !appliedTypes.includes(entry.id),
        onActive: (id: string) => toggleType(id, true),
        onInactive: (id: string) => toggleType(id, false),
      };
    });
  }, [
    typeCounts,
    multiProductTypes,
    productsInUse,
    isLoadingProductsInUse,
    isLoadingTypeCounts,
    quickTypeNames,
    typeOptions,
    appliedTypes,
    appliedProducts,
    toggleType,
    toggleProduct,
  ]);

  // Build server-side filter
  const hardwareFilter = useMemo((): HardwareQueryFilter | undefined => {
    if (!system.gid) return undefined;
    const filter: HardwareQueryFilter = { systemId: system.gid };
    if (appliedStates.length > 0) {
      filter.state = appliedStates;
    }
    if (appliedTypes.length > 0) {
      filter.hardwareTypeName = appliedTypes;
    }
    if (appliedProducts.length > 0) {
      filter.hardwareProductId = appliedProducts;
    }
    if (isYesNo(filters.available)) {
      filter.isAvailable = filters.available === 'true';
    }
    if (isYesNo(filters.online)) {
      filter.isOnline = filters.online === 'true';
    }
    if (isYesNo(filters.inRental)) {
      filter.isInRental = filters.inRental === 'true';
    }
    if (debouncedSearchTerm) {
      filter.search = debouncedSearchTerm;
    }
    return filter;
  }, [
    system.gid,
    appliedStates,
    appliedTypes,
    appliedProducts,
    filters.available,
    filters.online,
    filters.inRental,
    debouncedSearchTerm,
  ]);

  // Fetch hardware using non-Suspense hook with server-side pagination
  const {
    data: relayData,
    count: totalHardwareCount,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading: isLoadingHardware,
    isRefetching,
    refetch,
  } = useHardwareRelayQuery(hardwareFilter, {
    limit: filters.sizePerPage,
    after,
    sort: sortConfig,
    enabled: systemLoaded && isCompleted && Boolean(system.gid),
  });

  // Only the current page's rows are shown (server-side pagination).
  const hardwareItems = useMemo(() => {
    if (!relayData) return [];
    return relayData.map(mapHardwareToIHardware);
  }, [relayData]);

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relayData]);

  // has_safety_ticket is mapped from the GraphQL hasOpenSafetyTicket field
  const warningMessageForSafetyTickets = useMemo(() => {
    const hardwareWithSafetyTickets = hardwareItems?.some(
      (hardware: IHardware) => hardware.has_safety_ticket
    );
    if (hardwareWithSafetyTickets) {
      return 'Hardware with safety-critical ticket are unavailable for rental until ticket is resolved.';
    }
  }, [hardwareItems]);

  const hardwareColumns = useMemo(() => {
    return hardwareColumnsV2(
      type,
      system.hardware_type,
      remoteUnlockId,
      remoteLockId,
      remoteRingId,
      remoteActionLoaded,
      (row: IHardware, item: IStatePillsOption) => {
        // @ts-ignore
        customAsyncToast({
          promise: () =>
            updateHardware(
              {
                id: row.old_id,
                state: item.value,
              },
              {
                hardware_type: type,
              }
            ),
          loadingMessage: 'Updating...',
          successMessage: () => {
            return 'Hardware updated successfully!';
          },
          errorMessage: 'Failed to update hardware',
        }).then(() => refetch());
      },
      isMobileDevice,
      isMediumScreen,
      hardwareAccess,
      false
    ) as IColumn[];
  }, [
    type,
    system.hardware_type,
    remoteUnlockId,
    remoteLockId,
    remoteRingId,
    remoteActionLoaded,
    refetch,
    isMobileDevice,
    isMediumScreen,
    hardwareAccess,
  ]);

  const isLoading =
    !systemLoaded || isLoadingHardwareTypes || !isCompleted || isLoadingHardware;

  // Every chip is derived from the applied filters rather than held in its own
  // state, so rebuilding one group can't silently drop another.
  const displayedChips = useMemo(() => {
    const result: SearchChip[] = [];
    appliedStates.forEach((value) => {
      result.push({
        label: 'State',
        field: 'status',
        value,
        displayValue: stateOptions.find((option) => option.value === Number(value))
          ?.label,
      });
    });
    appliedTypes.forEach((name) => {
      result.push({
        label: 'Type',
        field: 'types',
        value: name,
        displayValue: labelForType(name),
      });
    });
    appliedProducts.forEach((productId) => {
      result.push({
        label: 'Product',
        field: 'products',
        value: productId,
        displayValue:
          productsInUse.find((product) => product.id === productId)?.name ?? 'Product',
      });
    });
    const yesNoChips: Array<[string, string, string]> = [
      ['Available', 'available', filters.available],
      ['Online', 'online', filters.online],
      ['In Rental', 'inRental', filters.inRental],
    ];
    yesNoChips.forEach(([label, field, value]) => {
      if (!isYesNo(value)) return;
      result.push({
        label,
        field,
        value,
        displayValue: yesNoOptions.find((option) => option.value === value)?.label,
      });
    });
    return result;
  }, [
    appliedStates,
    appliedTypes,
    appliedProducts,
    productsInUse,
    filters.available,
    filters.online,
    filters.inRental,
    labelForType,
  ]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      // PaginationTable groups chips and passes {label, values, disabled} here
      // (no `field`), so match on the chip label.
      const clearByLabel: Record<string, () => void> = {
        State: () => {
          if (queryParams.get('status')) deleteQueryParam('status');
          setPendingStates([]);
          setFilters((prevState: any) => ({ ...prevState, status: [] }));
        },
        Type: () => {
          if (queryParams.get('type')) deleteQueryParam('type');
          setPendingTypes([]);
          setFilters((prevState: any) => ({ ...prevState, types: [] }));
        },
        Product: () => {
          setFilters((prevState: any) => ({ ...prevState, products: [] }));
        },
        Available: () => {
          setAvailableValue('');
          setFilters((prevState: any) => ({ ...prevState, available: '' }));
        },
        Online: () => {
          setOnlineValue('');
          setFilters((prevState: any) => ({ ...prevState, online: '' }));
        },
        'In Rental': () => {
          setInRentalValue('');
          setFilters((prevState: any) => ({ ...prevState, inRental: '' }));
        },
      };

      clearByLabel[deletedGroup.label]?.();
      resetPagination();
    },
    [deleteQueryParam, queryParams, setFilters, resetPagination]
  );

  return (
    <PageContainer
      documentationLink={'https://documentation.movatic.co/admin-site/modes'}
      spaceName={'Hardware'}
      warningMessage={warningMessageForSafetyTickets}
    >
      <Card>
        <PaginationTable
          showFilter
          totalItems={totalHardwareCount}
          hasMore={graphqlHasMore}
          noDataSlice
          localSearch={false}
          chips={displayedChips}
          isLoading={isLoading || isPaginating}
          isLoadingCache={isRefetching}
          handleChipDelete={handleChipDelete}
          exportData={exportData}
          csvFileName={'Hardware.csv'}
          buttons={[
            {
              startIcon: <StoreIcon />,
              label: 'Buy',
              onClick: () => window.open(MOVATIC_SHOP, '_blank'),
            },
            {
              startIcon: <AddIcon />,
              label: 'Add',
              onClick: () => history.push('/hardware/add'),
              disabled: !hardwareAccess,
            },
            {
              label: 'Export',
              startIcon: <FileDownloadIcon />,
              onClick: () => setExportData(true),
            },
            {
              label: 'Import',
              startIcon: <FileUploadIcon />,
              onClick: () => history.push('/hardware/add_csv'),
            },
          ]}
          onExport={() => setExportData(false)}
          onApplyFilter={() => {
            // Applying is a straight copy of the pending controls; every chip is
            // derived from these values.
            if (queryParams.get('status') && pendingStates.length === 0) {
              deleteQueryParam('status');
            }
            if (queryParams.get('type') && pendingTypes.length === 0) {
              deleteQueryParam('type');
            }
            setFilters((prevState: any) => ({
              ...prevState,
              status: pendingStates,
              types: pendingTypes,
              available: availableValue,
              online: onlineValue,
              inRental: inRentalValue,
            }));
            resetPagination();
          }}
          spaceFilters={
            <>
              <MovaticFieldGroupSelectFilter
                label={'State'}
                value={pendingStates
                  .map((value) =>
                    stateOptions.find((option) => option.value === Number(value))
                  )
                  .filter(Boolean)}
                onChange={(event: React.SyntheticEvent, newValue: any[]) =>
                  setPendingStates(
                    newValue
                      .filter((option) =>
                        stateOptions.some((item) => item.value === option.value)
                      )
                      .map((option) => option.value)
                  )
                }
                options={stateOptions}
              />
              <MovaticFieldGroupSelectFilter
                label={'Type'}
                isLoading={isLoadingHardwareTypes}
                value={pendingTypes.map(
                  (name) =>
                    typeOptions.find((option) => option.value === name) ?? {
                      value: name,
                      label: labelForType(name),
                    }
                )}
                onChange={(event: React.SyntheticEvent, newValue: any[]) =>
                  setPendingTypes(newValue.map((option) => option.value))
                }
                options={typeOptions}
              />
              <MovaticFieldGroupSelect
                label={'Available'}
                value={availableValue}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setAvailableValue(event.target.value)
                }
                optionNodes={yesNoOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              />
              <MovaticFieldGroupSelect
                label={'Online'}
                value={onlineValue}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setOnlineValue(event.target.value)
                }
                optionNodes={yesNoOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              />
              <MovaticFieldGroupSelect
                label={'In Rental'}
                value={inRentalValue}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setInRentalValue(event.target.value)
                }
                optionNodes={yesNoOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              />
            </>
          }
          onRowCLick={(id: string) => history.push(`/hardware/${id}`)}
          sortBy={sorting.key}
          sortDirection={sorting.direction}
          onSortChange={(sort) => {
            resetPagination();
            setFilters((prevState: any) => ({
              ...prevState,
              sortConfig: sort as ISortConfig,
            }));
          }}
          padding={{ pt: 1, pl: 0, pr: 1 }}
          columns={hardwareColumns as IColumn[]}
          items={hardwareItems || []}
          searchPlaceholder={`Search`}
          page={pageNumber}
          rowsPerPage={filters.sizePerPage}
          quickFilters={
            // Never render with an empty list: MultiActionChip's seeding effect
            // spins on it (see needsActiveStateSeed). While the toggle data
            // settles, hold the row's space with pill skeletons so the final
            // chips appear in place instead of pushing the layout.
            quickTypeToggles.length > 0 ? (
              <MultiActionChip items={quickTypeToggles} />
            ) : isLoadingTypeCounts || isLoadingProductsInUse ? (
              <Stack direction="row" spacing={1}>
                {[0, 1, 2].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    width={96}
                    height={32}
                    sx={{ borderRadius: '16px' }}
                  />
                ))}
              </Stack>
            ) : undefined
          }
          noDataText={
            <>
              {totalHardwareCount === 0 ? (
                <Stack alignItems={'center'}>
                  <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                    <img
                      src={'/assets/hardware_banner.png'}
                      alt={''}
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                  <Button
                    sx={{
                      bgcolor:
                        theme.palette.mode === 'light'
                          ? '#101C3D'
                          : theme.palette.secondary.main,
                      '&:hover': {
                        textDecoration: 'none',
                        color: '#FFFFFF',
                      },
                    }}
                    disableRipple
                    href={MOVATIC_SHOP}
                    target="_blank"
                    startIcon={<StoreMallDirectoryIcon />}
                    variant="contained"
                  >
                    <Typography variant={'subtitle2'}>Build your fleet</Typography>
                  </Button>
                </Stack>
              ) : (
                <Typography variant={'subtitle1'}>No hardware found</Typography>
              )}
            </>
          }
          onPageChange={(page: any, newPage: number) => onPageChange(newPage)}
          onRowsPerPageChange={(pageSizeString) => {
            resetPagination();
            setFilters((prevState: any) => ({
              ...prevState,
              sizePerPage: Number(pageSizeString.target.value),
            }));
          }}
          handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
            resetPagination();
            setFilters((prevState: any) => ({ ...prevState, search: e.target.value }));
          }}
          searchText={filters.search}
        />
      </Card>
    </PageContainer>
  );
};

const ConnectedNewHardware = connect(
  (state: {
    hardware: {
      hardwareTypes: string[];
      remoteUnlockId: string;
      remoteLockId: string;
      remoteRingId: string;
      remoteActionLoaded: boolean;
    };
    mode: IMode;
    system: { current: ISystem; isLoaded: boolean };
    admin: { admin: IAuth; systemAccess: number };
  }) => ({
    systemAccess: state.admin.systemAccess,
    modeInfo: state.mode,
    system: state.system.current,
    remoteUnlockId: state.hardware.remoteUnlockId,
    remoteLockId: state.hardware.remoteLockId,
    remoteRingId: state.hardware.remoteRingId,
    remoteActionLoaded: state.hardware.remoteActionLoaded,
    auth: state.admin.admin,
    systemLoaded: state.system.isLoaded,
    movaticAccess: state.admin.admin.admin.movaticAccess,
  }),
  () => ({})
)(NewHardware);

export default withRouter(ConnectedNewHardware);
